import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  API_ENDPOINTS,
  CartModificationPayload,
  CompareCartData,
  CustomCart,
  OccCartModificationPayload,
  OccShareCartResponse,
} from '@cart/const';
import { HTTP_CONTEXT_ENDPOINT } from '@core/const';
import {
  Cart,
  CartModification,
  ConverterService,
  OCC_CART_ID_CURRENT,
  Occ,
  OccEndpointsService,
} from '@spartacus/core';
import { Observable, combineLatest, of, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { CART_MODIFICATION_NORMALIZER, CART_NORMALIZER } from '../converters';

@Injectable({
  providedIn: 'root',
})
export class CartApiService {
  constructor(
    private http: HttpClient,
    private occEndpointsService: OccEndpointsService,
    private converterService: ConverterService,
  ) {}

  validateCart(data: CompareCartData): Observable<CartModificationPayload> {
    const { cartId, newBaseSiteId, persistCart } = data;

    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.compareCart, {
      urlParams: { cartId },
      queryParams: { newBaseSiteId, persistCart },
    });

    return this.http
      .post<OccCartModificationPayload>(
        url,
        {},
        {
          context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.compareCart),
        },
      )
      .pipe(
        mergeMap((rawData: OccCartModificationPayload) =>
          combineLatest([
            of(rawData.cartModifications).pipe(this.converterService.pipeableMany(CART_MODIFICATION_NORMALIZER)),
            of(rawData.newBaseSiteId),
          ]).pipe(
            map(([cartModifications, baseSiteId]: [CartModification[], string]) => ({
              cartModifications,
              newBaseSiteId: baseSiteId,
            })),
          ),
        ),
        catchError((error: any) => throwError(() => error)),
      );
  }

  cloneCart(cartGuid: string): Observable<Cart> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.cloneCart, {
      queryParams: { cartGuid },
    });

    return this.http
      .post<Cart>(
        url,
        {},
        {
          context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.cloneCart),
        },
      )
      .pipe(
        this.converterService.pipeable(CART_NORMALIZER),
        catchError((error: any) => throwError(() => error)),
      );
  }

  shareCart(cartId: string): Observable<OccShareCartResponse> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.shareCart, {
      urlParams: { cartId },
    });

    return this.http.post<OccShareCartResponse>(
      url,
      {},
      {
        context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.shareCart),
      },
    );
  }

  removeAllEntries(_userId: string, cartId: string): Observable<unknown> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.removeAllEntries, {
      urlParams: { cartId },
    });

    return this.http.delete<unknown>(url, {
      context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.removeAllEntries),
    });
  }

  check(cartId: string, cartBaseStore: string): Observable<CustomCart> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.cart, {
      urlParams: { cartId },
    });

    return this.http
      .get<CustomCart>(url, {
        context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.cart),
        params: { baseStore: cartBaseStore },
      })
      .pipe(this.converterService.pipeable(CART_NORMALIZER));
  }

  setOrganization(cartId: string, organizationUid: string | null): Observable<void> {
    const url = this.occEndpointsService.buildUrl(API_ENDPOINTS.cart, {
      urlParams: { cartId },
      queryParams: { organizationUid },
    });

    return this.http.put<void>(url, null, {
      context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.cart),
    });
  }

  public loadAll(): Observable<Cart[]> {
    return this.http
      .get<Occ.CartList>(this.occEndpointsService.buildUrl(API_ENDPOINTS.carts), {
        context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.carts),
      })
      .pipe(
        map((res) => res.carts),
        this.converterService.pipeableMany(CART_NORMALIZER),
      );
  }

  public load(cartId: string, refresh?: boolean): Observable<Cart> {
    if (cartId === OCC_CART_ID_CURRENT) {
      return this.loadAll().pipe(
        map((carts) => {
          if (carts) {
            return carts.find((cart) => cart['saveTime'] === undefined);
          } else {
            return null;
          }
        }),
      );
    } else {
      const params: { refreshCart?: boolean } = {};

      if (refresh) {
        params.refreshCart = refresh;
      }

      return this.http
        .get<Occ.Cart>(
          this.occEndpointsService.buildUrl(API_ENDPOINTS.cart, {
            urlParams: { cartId },
          }),
          {
            params,
            context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.cart),
          },
        )
        .pipe(this.converterService.pipeable(CART_NORMALIZER));
    }
  }

  create(oldCartId?: string, toMergeCartGuid?: string): Observable<Cart> {
    const toAdd = JSON.stringify({});

    let params = {};

    if (oldCartId) {
      params = { oldCartId: oldCartId };
    }
    if (toMergeCartGuid) {
      params['toMergeCartGuid'] = toMergeCartGuid;
    }

    return this.http
      .post<Occ.Cart>(
        this.occEndpointsService.buildUrl(API_ENDPOINTS.createCart, {
          queryParams: params,
        }),
        toAdd,
        {
          context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.createCart),
        },
      )
      .pipe(this.converterService.pipeable(CART_NORMALIZER));
  }

  delete(_userId: string, cartId: string): Observable<unknown> {
    return this.http.delete<unknown>(
      this.occEndpointsService.buildUrl(API_ENDPOINTS.deleteCart, {
        urlParams: { cartId },
      }),
      { context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, API_ENDPOINTS.deleteCart) },
    );
  }
}
